import { callable } from './callable'

export type PeopleSavedViewId = 'all' | 'new-signups' | 'power-users' | 'users-with-restrictions'

export interface ListOwnerPeopleParams {
  search?: string
  plan?: string
  role?: string
  hasOpenTickets?: boolean
  hasRestrictions?: boolean
  hasCredits?: boolean
  createdAfter?: string
  createdBefore?: string
  savedView?: PeopleSavedViewId
  sortBy?: 'lastActiveAt' | 'createdAt' | 'openTicketCount' | 'creditSpend'
  sortDir?: 'asc' | 'desc'
  cursor?: string | null
  limit?: number
}

export interface OwnerPersonListItem {
  uid: string
  email: string
  displayName: string
  plan: string
  createdAt: string
  lastActiveAt: string | null
  openTicketCount: number
  hasRestrictions: boolean
  workspacesOwnedCount: number
  credits: {
    personalAvailable: number
  }
}

export interface ListOwnerPeopleResult {
  people: OwnerPersonListItem[]
  nextCursor: string | null
  totalCount: number | null
  effectiveOrder?: Array<{ field: string; direction: 'asc' | 'desc' }>
  orderingChanged?: boolean
  boundedSearch?: { cap: number; truncated: boolean; prefixComplete?: boolean } | null
}

export interface OwnerPersonWorkspaceItem {
  id: string
  name: string
  role: string
  plan: string
  lastActiveAt: string | null
  alerts: string
  seatCount: number
}

export interface OwnerPersonWalletSummary {
  availableCredits: number
  lifetimeSpentCredits: number
  lifetimeGrantedCredits: number
  lifetimePurchasedCredits: number
  lastGrantedAt: string | null
}

export interface OwnerPersonFeedbackItem {
  id: string
  formId: string
  rating: number | null
  submittedAt: string | null
  remarks: string | null
  remarkRedacted: boolean
  requiresBreakGlass: boolean
}

export interface OwnerPersonBreakGlassGrantItem {
  grantId: string
  reason: string
  linkedId: string | null
  durationSec: number
  actorEmail: string
  actorRole: string
  createdAt: string | null
}

export interface OwnerPersonDetailBundle {
  person: {
    uid: string
    email: string
    displayName: string
    plan: string
    disabled: boolean
    createdAt: string
    lastActiveAt: string | null
    lastSignInTime: string | null
    lastRefreshTime: string | null
  }
  kpi: {
    workspacesOwned: number
    workspacesMemberOf: number
    creditsAvailable: number
    creditsSpentLifetime: number
    openTickets: number
  }
  wallet: OwnerPersonWalletSummary
  workspaces: OwnerPersonWorkspaceItem[]
  feedback: OwnerPersonFeedbackItem[]
  breakglassGrants: OwnerPersonBreakGlassGrantItem[]
  inTheirWords: {
    latestFeedbackHeadline: string | null
    latestTicketSubject: string | null
  }
  recentActions: Array<{
    id: string
    kind: string
    status: string
    actorId: string
    actorRole: string
    reason: string
    createdAt: string
  }>
  restrictions: OwnerPersonRestrictions | null
}

export interface OwnerPersonRestrictions {
  blockNewTickets?: boolean
  blockReopens?: boolean
  slowMode?: number | null
  autoTag?: string[]
  skipAutoP0?: boolean
  broadcastOptOut?: boolean
  reason?: string | null
  [key: string]: unknown
}

export async function listOwnerPeople(params: ListOwnerPeopleParams = {}): Promise<ListOwnerPeopleResult> {
  const res = await callable<ListOwnerPeopleParams, { ok: boolean; people: OwnerPersonListItem[]; nextCursor: string | null; totalCount: number | null; effectiveOrder?: ListOwnerPeopleResult['effectiveOrder']; orderingChanged?: boolean; boundedSearch?: ListOwnerPeopleResult['boundedSearch'] }>('listOwnerPeople', params)
  return {
    people: res.people || [],
    nextCursor: res.nextCursor || null,
    totalCount: res.totalCount ?? null,
    effectiveOrder: res.effectiveOrder,
    orderingChanged: res.orderingChanged,
    boundedSearch: res.boundedSearch,
  }
}

export async function getOwnerPersonDetail(
  uid: string,
  revealToken?: string,
  sessionId?: string,
  accessReason?: unknown,
): Promise<OwnerPersonDetailBundle> {
  const res = await callable<
    { uid: string; revealToken?: string; sessionId?: string; accessReason?: unknown },
    { ok: boolean } & OwnerPersonDetailBundle
  >('getOwnerPersonDetail', { uid, revealToken, sessionId, accessReason })
  return {
    person: res.person,
    kpi: res.kpi,
    wallet: res.wallet || {
      availableCredits: res.kpi?.creditsAvailable || 0,
      lifetimeSpentCredits: res.kpi?.creditsSpentLifetime || 0,
      lifetimeGrantedCredits: 0,
      lifetimePurchasedCredits: 0,
      lastGrantedAt: null,
    },
    workspaces: res.workspaces || [],
    feedback: res.feedback || [],
    breakglassGrants: res.breakglassGrants || [],
    inTheirWords: res.inTheirWords,
    recentActions: res.recentActions || [],
    restrictions: res.restrictions || null,
  }
}
